import type { LvaItemListRow } from '@dcc-erp/shared';

import { Can } from '../Can';

// Router-free, presentational quick-action cell for the LVA detail footer.
// Each action is RBAC-gated via <Can> (UX-only — the server enforces) and
// disabled once the item is DISPOSED (a closed item takes no further lifecycle
// actions). Extracted from DetailModal so the gating/disabled branches are
// unit-testable without a router or QueryClient (sibling pattern: ApBillRowActions).
export interface LvaDetailActionsProps {
  item: LvaItemListRow;
  busy: boolean;
  onTransfer: () => void;
  onReassign: () => void;
  onMarkDamaged: () => void;
  onMarkLost: () => void;
  onDispose: () => void;
}

export function LvaDetailActions({
  item,
  busy,
  onTransfer,
  onReassign,
  onMarkDamaged,
  onMarkLost,
  onDispose,
}: LvaDetailActionsProps) {
  const isDisposed = item.status === 'DISPOSED';
  // transfer / reassign / mark-damaged / mark-lost are rejected server-side for
  // BOTH DISPOSED and LOST items (loadActiveItem) — so they disable on either.
  // dispose is the exception: a LOST item is still disposable (LOST is
  // near-terminal but not closed), so it only disables once already DISPOSED.
  const actionDisabled = isDisposed || item.status === 'LOST' || busy;
  const disposeDisabled = isDisposed || busy;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      <Can permission="lva.transfer">
        <button className="btn sm" type="button" onClick={onTransfer} disabled={actionDisabled}>
          โอนย้าย
        </button>
      </Can>
      <Can permission="lva.reassign">
        <button className="btn sm" type="button" onClick={onReassign} disabled={actionDisabled}>
          เปลี่ยนผู้ถือครอง
        </button>
      </Can>
      <Can permission="lva.markDamaged">
        <button className="btn sm" type="button" onClick={onMarkDamaged} disabled={actionDisabled}>
          แจ้งชำรุด
        </button>
      </Can>
      <Can permission="lva.markLost">
        <button
          className="btn sm"
          type="button"
          style={{ color: 'var(--negative)' }}
          onClick={onMarkLost}
          disabled={actionDisabled}
        >
          แจ้งสูญหาย
        </button>
      </Can>
      <Can permission="lva.dispose">
        <button
          className="btn sm"
          type="button"
          style={{ color: 'var(--negative)' }}
          onClick={onDispose}
          disabled={disposeDisabled}
        >
          {isDisposed ? 'จำหน่ายแล้ว' : 'จำหน่าย / ขายซาก'}
        </button>
      </Can>
    </div>
  );
}
